import type { ColumnType } from './table-file.service';
import type { WorkbookSheet, WorkbookSheetProblem } from './workbook-reader';

export type { WorkbookSheet, WorkbookSheetProblem } from './workbook-reader';
export { readWorkbookSheets, readWorksheet } from './workbook-reader';

/**
 * Multi-sheet and multi-file support for the internal-table upload.
 *
 * One selected sheet becomes one table; a CSV file counts as one sheet. Reading a workbook
 * lives in `workbook-reader.ts` (the worker uses it) and reading the selected files in
 * `upload-files-reader.service.ts`; this file turns what they read into what the picker
 * lists and what the add-tables dialog edits and uploads. Everything here is pure.
 */

/** The file types the table upload accepts. */
export type UploadFileKind = 'csv' | 'xlsx';

/** One selected file and the sheets read from it (a CSV yields exactly one). */
export interface UploadSourceFile {
    fileName: string;
    kind: UploadFileKind;
    sizeKB: number;
    sheets: WorkbookSheet[];
    /** Set when the whole file could not be read; its sheets list is then empty. */
    error: string | null;
}

/** One file in the picker, with its sheets summarised. Sheet `index` runs across all files. */
export interface SheetPickerGroup {
    fileName: string;
    kind: UploadFileKind;
    sheets: WorkbookSheetSummary[];
    error: string | null;
}

/** What the sheet picker lists for one sheet. */
export interface WorkbookSheetSummary {
    index: number;
    name: string;
    /** Data rows below the header row. */
    rowCount: number;
    columnCount: number;
    previewHeaders: string[];
    previewRows: string[][];
    error: WorkbookSheetProblem | null;
}

/** Everything a user can edit for one sheet before it is written as a table. */
export interface SheetDraft {
    /** Unique within one upload (`<file>:<sheet>`); sheet names repeat across files. */
    key: string;
    /** Tab label: the sheet name for a multi-sheet workbook, else the file name. */
    label: string;
    fileName: string;
    /** This sheet's share of its file's size, sent as the upload size. */
    sizeKB: number;
    sheetName: string;
    name: string;
    description: string;
    headers: string[];
    rows: unknown[][];
    types: ColumnType[];
    /** Unit per column; for a DATE column this is its date format (null until chosen). */
    units: (string | null)[];
    editStates: boolean[];
    /** Headers renamed when the sheet was read because their names are reserved. */
    renamedHeaders: { from: string; to: string }[];
}

/** The fields of a draft the dialog edits, captured when the user leaves that sheet. */
export type SheetDraftEdits = Pick<
    SheetDraft,
    'name' | 'description' | 'headers' | 'types' | 'units' | 'editStates' | 'renamedHeaders'
>;

function previewCell(value: unknown): string {
    return value === null || value === undefined ? '' : String(value);
}

/** Name, size and a short preview of each sheet, for the picker. */
export function summarizeWorkbookSheets(sheets: WorkbookSheet[], previewRowCount = 3): WorkbookSheetSummary[] {
    return sheets.map((sheet, index) => {
        const headerRow = (sheet.data[0] ?? []) as unknown[];
        const columnCount = headerRow.length;
        const previewHeaders = Array.from({ length: columnCount }, (_, i) => previewCell(headerRow[i]));
        const previewRows = sheet.data
            .slice(1, 1 + previewRowCount)
            .map(row => previewHeaders.map((_, i) => previewCell((row as unknown[])?.[i])));
        return {
            index,
            name: sheet.name,
            rowCount: Math.max(0, sheet.data.length - 1),
            columnCount,
            previewHeaders,
            previewRows,
            error: sheet.error,
        };
    });
}

/** Lowercase, underscores for anything else, and a leading underscore before a digit. */
export function toTableNameSlug(base: string): string {
    return String(base ?? '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/^(\d)/, '_$1');
}

/** What `buildSheetDraft` needs from `TableFileService`. */
export interface SheetDraftFileRules {
    rowsFromXlsxAoA(aoa: string[][]): { headers: string[]; rows: unknown[][] };
    inferTypesFromSample(headers: string[], rows: unknown[][]): ColumnType[];
    renameReservedHeaders(headers: string[]): { headers: string[]; renamed: { from: string; to: string }[] };
}

/** The table-name rules' length limit. */
const TABLE_NAME_MAX = 63;

/** `toTableNameSlug(base)` made unique against `taken` with `_2`, `_3`, within 63 characters. Adds the result to `taken`. */
export function uniqueTableName(base: string, taken: Set<string>): string {
    const slug = (toTableNameSlug(base) || 'table').slice(0, TABLE_NAME_MAX);
    let name = slug;
    for (let n = 2; taken.has(name.toLowerCase()); n++) {
        const suffix = `_${n}`;
        name = slug.slice(0, TABLE_NAME_MAX - suffix.length) + suffix;
    }
    taken.add(name.toLowerCase());
    return name;
}

/** The file name without its extension. */
export function fileBaseName(fileName: string): string {
    const name = String(fileName ?? '');
    const dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(0, dot) : name;
}

/** CSV or Excel by extension, falling back to the MIME type (Windows often reports a CSV as `application/vnd.ms-excel`). */
export function uploadFileKind(file: { name: string; type: string }): UploadFileKind | null {
    const name = String(file.name ?? '').toLowerCase();
    if (name.endsWith('.csv')) return 'csv';
    if (name.endsWith('.xlsx')) return 'xlsx';
    if (file.type === 'text/csv') return 'csv';
    if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'xlsx';
    return null;
}

/**
 * A parsed CSV (one object per row, keyed by header) as a sheet. A header row the parser
 * could not read comes back as positional keys (`0`, `1`, ...), which is the single-file
 * upload's "missing header" case.
 */
export function csvObjectsToSheet(name: string, rows: Record<string, unknown>[]): WorkbookSheet {
    if (!rows.length) {
        return { name, data: [], error: 'no data' };
    }
    const headers = Object.keys(rows[0]);
    const data: unknown[][] = [headers, ...rows.map(r => headers.map(h => r[h]))];
    if (headers.some(k => Number.isInteger(Number(k)))) {
        return { name, data, error: 'missing header' };
    }
    return { name, data, error: null };
}

/** The picker's groups: one per file, sheet indexes numbered across every file in order. */
export function buildPickerGroups(files: UploadSourceFile[], previewRowCount = 3): SheetPickerGroup[] {
    let offset = 0;
    return files.map((f) => {
        const sheets = summarizeWorkbookSheets(f.sheets, previewRowCount).map(s => ({ ...s, index: s.index + offset }));
        offset += f.sheets.length;
        return { fileName: f.fileName, kind: f.kind, sheets, error: f.error };
    });
}

/**
 * Drafts for the sheets chosen in the picker (`indexes` as numbered by `buildPickerGroups`).
 * A sheet of a multi-sheet workbook is named after the sheet, any other after its file. Default
 * names never repeat each other or a name in `existingNames`. Each draft carries its share of its
 * file's size, split by row count, so several sheets of one file do not each count the whole file.
 */
export function draftsForChosenSheets(
    files: UploadSourceFile[],
    indexes: number[],
    rules: SheetDraftFileRules,
    existingNames: Iterable<string> = []
): SheetDraft[] {
    const refs = files.flatMap((f, fileIndex) => f.sheets.map((sheet, sheetIndex) => ({ f, fileIndex, sheet, sheetIndex })));
    const taken = new Set([...existingNames].map(n => String(n).toLowerCase()));
    return indexes
        .map(i => refs[i])
        .filter(Boolean)
        .map(({ f, fileIndex, sheet, sheetIndex }) => {
            const label = f.sheets.length > 1 ? sheet.name : fileBaseName(f.fileName);
            const fileRows = f.sheets.reduce((n, s) => n + Math.max(1, s.data.length), 0);
            const sizeKB = +((f.sizeKB * Math.max(1, sheet.data.length)) / Math.max(1, fileRows)).toFixed(2);
            return buildSheetDraft(sheet, rules, {
                key: `${fileIndex}:${sheetIndex}`,
                label,
                fileName: f.fileName,
                sizeKB,
                name: uniqueTableName(label, taken),
            });
        });
}

/** Tab tooltip: "file / sheet" for a sheet of a multi-sheet workbook, else just the file name. */
export function sheetDraftTitle(draft: Pick<SheetDraft, 'label' | 'fileName' | 'sheetName'>): string | null {
    if (!draft.fileName) {
        return null;
    }
    const fromWorkbookSheet = draft.label === draft.sheetName && draft.label !== fileBaseName(draft.fileName);
    return fromWorkbookSheet ? `${draft.fileName} / ${draft.sheetName}` : draft.fileName;
}

/** Builds the starting draft of a sheet: cleaned rows, inferred types, default units. */
export function buildSheetDraft(
    sheet: WorkbookSheet,
    rules: SheetDraftFileRules,
    source?: Pick<SheetDraft, 'key' | 'label' | 'fileName' | 'sizeKB' | 'name'>
): SheetDraft {
    const parsed = rules.rowsFromXlsxAoA(sheet.data as string[][]);
    const { headers, renamed } = rules.renameReservedHeaders(parsed.headers);
    const rows = parsed.rows;
    const types = rules.inferTypesFromSample(headers, rows);
    return {
        key: source?.key ?? sheet.name,
        label: source?.label ?? sheet.name,
        fileName: source?.fileName ?? '',
        sizeKB: source?.sizeKB ?? 0,
        sheetName: sheet.name,
        name: source?.name ?? toTableNameSlug(sheet.name),
        description: '',
        headers,
        rows,
        types,
        units: types.map(t => (t === 'DATE' ? null : 'No Unit')),
        editStates: headers.map(() => false),
        renamedHeaders: renamed,
    };
}

/**
 * The drafts of one workbook upload and which one the dialog is showing. The dialog
 * keeps editing its usual form for the active sheet; switching sheets stores those
 * edits on the draft being left and hands back the draft being opened.
 */
export class SheetDraftSet {
    activeIndex = 0;

    constructor(public drafts: SheetDraft[]) {}

    get active(): SheetDraft | undefined {
        return this.drafts[this.activeIndex];
    }

    /** Copies the dialog's current edits onto the active draft. */
    store(edits: SheetDraftEdits): void {
        const draft = this.active;
        if (!draft) {
            return;
        }
        draft.name = edits.name;
        draft.description = edits.description;
        draft.headers = [...edits.headers];
        draft.types = [...edits.types];
        draft.units = [...edits.units];
        draft.editStates = [...edits.editStates];
        draft.renamedHeaders = [...edits.renamedHeaders];
    }

    /** Stores `edits` on the sheet being left, then returns the sheet at `index`. */
    switchTo(index: number, edits: SheetDraftEdits): SheetDraft {
        this.store(edits);
        this.activeIndex = Math.min(Math.max(index, 0), this.drafts.length - 1);
        return this.drafts[this.activeIndex];
    }

    /** Drops the drafts (by `key`) whose tables were written, so a retry only sends the rest. */
    removeSheets(keys: string[]): void {
        const done = new Set(keys);
        this.drafts = this.drafts.filter(d => !done.has(d.key));
        this.activeIndex = 0;
    }
}

/** Index of every draft whose table name another selected sheet also uses. */
export function duplicateDraftNameIndexes(drafts: Pick<SheetDraft, 'name'>[]): number[] {
    const counts = new Map<string, number>();
    for (const d of drafts) {
        const key = String(d.name ?? '').toLowerCase();
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return drafts
        .map((d, i) => ((counts.get(String(d.name ?? '').toLowerCase()) ?? 0) > 1 ? i : -1))
        .filter(i => i !== -1);
}

/** The create-table body the backend reads from the gzip: one table's schema, units and date formats. */
export function buildTableSchemaPayload(
    headers: string[],
    types: (ColumnType | null | undefined)[],
    units: (string | null | undefined)[],
    cleanHeaderKey: (h: string) => string
): {
    table: Record<string, ColumnType>;
    comments: Record<string, string>;
    dateColumns: Record<string, string>;
} {
    const table: Record<string, ColumnType> = {};
    const comments: Record<string, string> = {};
    const dateColumns: Record<string, string> = {};

    headers.forEach((h, i) => {
        const key = cleanHeaderKey(h);
        const typ = types[i] ?? 'TEXT';
        table[key] = typ;

        const unit = (units[i] ?? 'No Unit') as string;
        comments[key] = unit;

        if (typ === 'DATE') {
            dateColumns[key] = unit && unit !== 'No Unit' ? unit : 'default';
        }
    });

    return { table, comments, dateColumns };
}
